import React from 'react';
import {Navigate, Outlet, Route, Routes, useParams} from 'react-router-dom';
import Providers from 'src/app/provider/provider';
import {ManagedUIContext} from '@contexts/ui.context';
import ModernLayout from '@layouts/modern/layout';
import ManagedModal from '@components/common/modal/managed-modal';
import ManagedDrawer from '@components/common/drawer/managed-drawer';
import ToasterProvider from 'src/app/provider/toaster-provider';
import ScrollToTop from '@components/common/scroll-to-top';
import {fallbackLng, languages} from './app/i18n/settings';
import {useRouteLang} from './hooks/use-route-lang';
import NotFound from './app/not-found';

import ProductDetailPage from 'src/app/[lang]/(default)/products/[slug]/page';
import MyAccountOrdersPage from 'src/app/[lang]/(default)/my-account/orders/page';
import MyAccountWishlistPage from 'src/app/[lang]/(default)/my-account/wishlist/page';
import MyAccountNotificationPage from 'src/app/[lang]/(default)/my-account/notification/page';
import MyAccountChangePasswordPage from 'src/app/[lang]/(default)/my-account/change-password/page';
import MyAccountHelpCenterPage from 'src/app/[lang]/(default)/my-account/help-center/page';
import MyAccountLegalNoticePage from 'src/app/[lang]/(default)/my-account/legal-notice/page';
import MyAccountAccountSettingsPage from 'src/app/[lang]/(default)/my-account/account-settings/page';
import MyAccountAddressPage from 'src/app/[lang]/(default)/my-account/address/page';
import ContactUsPage from 'src/app/[lang]/(default)/contact-us/page';
import FaqPage from 'src/app/[lang]/(default)/faq/page';
import ImpressumPage from 'src/app/[lang]/(default)/impressum/page';
import HomePage from "src/app/[lang]/home/homePage";
import AccountLayout from './app/[lang]/(default)/my-account/AccountLayout';
import ProductsPageContent from "src/app/[lang]/(default)/products/products-page-content";
import AboutPageContent from "src/app/[lang]/(default)/about-us/about-page-content";
import AgbPageContent from "src/app/[lang]/(default)/agb/agb-page-content";
import TermsPageContent from "src/app/[lang]/(default)/terms/terms-page-content";
import PrivacyPageContent from './app/[lang]/(default)/privacy/privacy-page-content';
import OrderContentPage from './app/[lang]/(default)/orders/[slug]/order-content';
import {useTranslation} from "react-i18next";
import CheckoutPageContent from "src/app/[lang]/(default)/checkout/checkout-page-content";

// Language wrapper for context and validation
const LanguageWrapper: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {lang} = useParams<{ lang: string }>();
    const routeLang = useRouteLang();

    if (!lang || !languages.includes(lang)) {
        return <Navigate to={`/${fallbackLng}`} replace/>;
    }

    return (
        <Providers>
            <ManagedUIContext lang={routeLang}>
                <ModernLayout lang={routeLang}>
                    <ScrollToTop />
                    {children}
                    <ManagedModal lang={routeLang}/>
                    <ManagedDrawer lang={routeLang}/>
                    <ToasterProvider/>
                </ModernLayout>
            </ManagedUIContext>
        </Providers>
    );
};

const App: React.FC = () => {
    const {i18n} = useTranslation()
    return (
        <Routes>
            <Route path="/" element={<Navigate to={`/${i18n.language}`} replace/>}/>
            <Route path=":lang" element={<LanguageWrapper><Outlet/></LanguageWrapper>}>
                {/* Home */}
                <Route index element={<HomePage/>}/>

                {/* Products */}
                <Route path="products" element={<ProductsPageContent/>}/>
                <Route path="products/:slug" element={<ProductDetailPage/>}/>

                {/* Orders */}
                <Route path="orders/:slug" element={<OrderContentPage/>}/>

                <Route path="checkout" element={<CheckoutPageContent/>}/>

                {/* My Account */}
                <Route path="my-account" element={<AccountLayout><Outlet/></AccountLayout>}>
                    <Route path="orders" element={<MyAccountOrdersPage/>}/>
                    <Route path="wishlist" element={<MyAccountWishlistPage/>}/>
                    <Route path="notification" element={<MyAccountNotificationPage/>}/>
                    <Route path="change-password" element={<MyAccountChangePasswordPage/>}/>
                    <Route path="help-center" element={<MyAccountHelpCenterPage/>}/>
                    <Route path="legal-notice" element={<MyAccountLegalNoticePage/>}/>
                    <Route path="account-settings" element={<MyAccountAccountSettingsPage/>}/>
                    <Route path="address" element={<MyAccountAddressPage/>}/>
                </Route>

                {/* Static Pages */}
                <Route path="contact-us" element={<ContactUsPage/>}/>
                <Route path="faq" element={<FaqPage/>}/>
                <Route path="about-us" element={<AboutPageContent/>}/>
                <Route path="privacy" element={<PrivacyPageContent/>}/>
                <Route path="impressum" element={<ImpressumPage/>}/>
                <Route path="agb" element={<AgbPageContent/>}/>
                <Route path="terms" element={<TermsPageContent/>}/>

                {/* 404 */}
                <Route path="*" element={<NotFound/>}/>
            </Route>
        </Routes>
    );
};

export default App; 